<template>
	<div class="card !shadow-none !border-0">
		<div v-if="isLoading" class="flex items-center justify-center py-10">
			<i class="tabler:loader-2 w-8 h-8 animate-spin text-primary"></i>
			<span class="ml-2">Loading assessment...</span>
		</div>

		<div v-else-if="fetchError" class="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:alert-circle text-red-600 text-lg mr-2"></i>
				<span class="text-red-800">{{ fetchError }}</span>
			</div>
		</div>

		<div v-else-if="totalQuestions === 0" class="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
			<div class="flex items-center justify-between gap-3">
				<div class="flex items-center">
					<i class="tabler:alert-triangle text-yellow-600 text-lg mr-2"></i>
					<span class="text-yellow-900">No assessment questions available.</span>
				</div>
				<EwButton label="Close" type="secondary" @click="handleClose" />
			</div>
		</div>

		<AssessmentResult
			v-else-if="isCompleted"
			:title="assessmentTitle"
			:totalQuestions="totalQuestions"
			:answeredQuestions="answeredCount"
			:correctAnswers="correctAnswers"
			:totalPoints="totalPoints"
			:earnedPoints="earnedPoints"
			:percentage="scorePercentage"
			:elapsedSeconds="elapsedSeconds"
			:passingPercent="passingPercent"
			:results="evaluationResults"
			:competencyScores="competencyScores"
			:questionTypeScores="questionTypeScores"
			:criticalLevelScores="criticalLevelScores"
			:attemptHistory="attemptHistory"
			:retakeCount="retakeCount"
			:assessmentRules="assessmentRules"
			@retake="handleRetake"
			@close="handleClose"
		/>

		<template v-else-if="!hasStarted">
			<div class="rounded-lg border border-gray-200 overflow-hidden">
				<header class="p-4 bg-gray-50 border-b border-gray-200">
					<h3 class="text-lg font-semibold text-gray-900">{{ assessmentTitle }}</h3>
					<p v-if="assessmentLeadText" class="text-sm text-gray-600 mt-1">{{ assessmentLeadText }}</p>
				</header>

				<section class="p-4 space-y-4">
					<div class="grid grid-cols-1 md:grid-cols-5 gap-3">
						<div class="rounded-md border border-gray-200 p-3 h-full my-auto flex">
							<div class="w-full flex items-center justify-between gap-2">
								<div class="text-xs text-gray-500 uppercase">Questions</div>
								<div class="text-base font-semibold text-gray-900 text-right whitespace-nowrap leading-none">{{ totalQuestions }}</div>
							</div>
						</div>
						<div class="rounded-md border border-gray-200 p-3 h-full my-auto flex">
							<div class="w-full flex items-center justify-between gap-2">
								<div class="text-xs text-gray-500 uppercase">Passing Score</div>
								<div class="text-base font-semibold text-gray-900 text-right whitespace-nowrap leading-none">{{ passingPercent }}%</div>
							</div>
						</div>
						<div class="rounded-md border border-gray-200 p-3 h-full my-auto flex">
							<div class="w-full flex flex-wrap items-center justify-between gap-2">
								<div class="text-xs text-gray-500 uppercase">Time Limit</div>
								<div class="text-base font-semibold text-gray-900 text-right whitespace-nowrap leading-none">{{ formattedTimeLimit }}</div>
							</div>
						</div>
						<div v-if="scoringPointsPerQuestion" class="rounded-md border border-gray-200 p-3 h-full my-auto flex">
							<div class="w-full flex items-center justify-between gap-2">
								<div class="text-xs text-gray-500 uppercase">Points / Question</div>
								<div class="text-base font-semibold text-gray-900 text-right whitespace-nowrap leading-none">{{ scoringPointsPerQuestion }}</div>
							</div>
						</div>
						<div v-if="scoringTotalScore" class="rounded-md border border-gray-200 p-3 h-full my-auto flex">
							<div class="w-full flex items-center justify-between gap-2">
								<div class="text-xs text-gray-500 uppercase">Total Score</div>
								<div class="text-base font-semibold text-gray-900 text-right whitespace-nowrap leading-none">{{ scoringTotalScore }}</div>
							</div>
						</div>
					</div>

					<AssessmentRulesPanel :assessmentRules="assessmentRules" />

					<div class="rounded-md border border-blue-100 bg-blue-50 p-3">
						<div class="text-sm text-blue-900 leading-relaxed">
							{{ userConsentText }}
						</div>
					</div>

					<label class="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer select-none">
						<input
							v-model="hasMonitoringConsent"
							type="checkbox"
							class="sr-only"
							aria-label="Monitoring consent"
						/>
						<span
							class="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors"
							:class="hasMonitoringConsent ? 'border-primary bg-primary text-white' : 'border-gray-400 bg-white text-transparent'"
						>
							<span class="text-xs font-bold leading-none">✓</span>
						</span>
						<span class="text-sm text-gray-800">
							I understand and consent to monitoring of this assessment session.
						</span>
					</label>
					<div class="text-xs" :class="hasMonitoringConsent ? 'text-green-700' : 'text-gray-600'">
						{{ hasMonitoringConsent ? 'Consent confirmed.' : 'Please check the box to enable Start Assessment.' }}
					</div>

					<div v-if="startValidationMessage" class="text-sm text-danger">
						{{ startValidationMessage }}
					</div>
				</section>

				<footer class="p-4 border-t border-gray-200 flex justify-end gap-2">
					<EwButton label="Close" type="secondary" @click="handleClose" />
					<EwButton
						label="Start Assessment"
						type="success"
						iconAppend="tabler:player-play"
						:disabled="!hasMonitoringConsent"
						@click="startAssessment"
					/>
				</footer>
			</div>
		</template>

		<template v-else>
			<div class="rounded-lg border border-gray-200 overflow-hidden">
				<header class="p-4 bg-gray-50 border-b border-gray-200">
					<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
						Assessment Progress
					</div>
					<div class="mt-2 flex items-start">
						<div class="w-[90%] flex  gap-3 flex-col">
							<div class="mt-1 text-sm font-semibold text-gray-800">
								{{ labels.questionLabel || 'Question' }} {{ currentQuestionNumber }} / {{ totalQuestions }}
							</div>
							<div class="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
							<div class="h-2 bg-primary transition-all duration-300" :style="{ width: progressWidth }"></div>
							</div>
					<div class="mt-2 text-xs text-gray-600">Answered: {{ answeredCount }} / {{ totalQuestions }}</div>

						</div>
						<div class="w-[10%] text-right flex flex-col gap-3">
							<div class="text-[11px] uppercase tracking-wide text-gray-500">Elapsed</div>
							<div class="text-xl font-semibold leading-none text-gray-900 tabular-nums">{{ formattedElapsed }}</div>
						</div>
					</div>
				</header>

				<section class="p-4">
					<div v-if="showQuestionFullSliderEdit" class="flex items-start gap-2">
						<h4 class="text-base font-semibold text-gray-900 leading-relaxed">
							{{ currentQuestionNumber }}. {{ currentQuestion?.question || '' }}
						</h4>
						<EditButton
							class="shrink-0 rounded hover:bg-secondary-500/80 items-center py-1 px-2 text-center font-sans text-xs font-bold hover:text-secondary-500 transition-all bg-secondary !text-white"
							:id="currentQuestionEditId"
							type="lngReviewEdit"
							:overrideURL="currentQuestionEditUrl"
							headerTitle="Edit Question"
							toolTipLabel="Edit"
						/>
					</div>
					<InplaceEdit
						v-else
						:inputProp="{ propName: 'content', inputType: 'quiz', content: currentQuestion }"
						method="PUT"
						:href="currentQuestion?._links?.self?.href"
						blockTitle="Edit Question"
						:showAssistant="true"
					>
						<h4 class="text-base font-semibold text-gray-900 leading-relaxed">
							{{ currentQuestionNumber }}. {{ currentQuestion?.question || '' }}
						</h4>
					</InplaceEdit>
					<div v-if="hasCurrentQuestionMeta" class="mt-2 flex flex-wrap items-start gap-2">
						<span v-if="currentQuestionMeta.pointsThreshold" class="rounded bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">
							Points: {{ currentQuestionMeta.pointsThreshold }}
						</span>
						<span
							v-if="currentQuestionMeta.criticality"
							class="ml-auto rounded px-2 py-0.5 text-[11px] border whitespace-nowrap"
							:class="currentCriticalityBadgeClass"
						>
							Criticality: {{ currentQuestionMeta.criticality }}
						</span>
					</div>

					<div v-if="hasDisplayableQuestionImage" class="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
						<InteractiveImage :alt="displayedQuestionImageAlt" preview class="rounded">
							<template #indicatoricon>
								<i class="tabler:eye w-8 h-8 text-white"></i>
							</template>
							<template #image>
								<div class="w-full h-[180px] md:h-[220px] rounded overflow-hidden bg-gray-100">
									<img
										:src="currentQuestionImageSrc"
										:alt="displayedQuestionImageAlt"
										class="w-full h-full object-contain"
										@error="handleQuestionImageError"
									/>
								</div>
							</template>
							<template #preview="slotProps">
								<div class="p-5 w-[70%] mx-auto">
									<div v-if="hasQuestionImageTitle" class="text-mb mb-3 font-bold text-white">
										{{ currentQuestionImageTitle }}
									</div>
									<img
										:src="currentQuestionImageSrc"
										:alt="displayedQuestionImageAlt"
										class="mx-auto max-h-[720px]"
										:style="slotProps.style"
										@error="handleQuestionImageError"
										@click="slotProps.onClick"
									/>
								</div>
							</template>
						</InteractiveImage>
						<div v-if="hasQuestionImageTitle" class="mt-2 text-xs text-gray-600 italic text-center fw-semibold">
							{{ currentQuestionImageTitle }}
						</div>
					</div>

					<p class="text-xs text-gray-600 mt-2">
						{{ currentQuestionIsMulti ? 'Select all correct answers.' : 'Select one correct answer.' }}
					</p>

					<div class="mt-4 space-y-2">
						<template
							v-for="(answer, answerIndex) in currentAnswers"
							:key="answer.id ?? `${currentQuestionNumber}-${answerIndex}`"
						>
							<label
								class="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors"
								:class="isSelected(currentQuestionIndex, answerIndex) ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
							>
								<input
									class="mt-1"
									:type="currentQuestionIsMulti ? 'checkbox' : 'radio'"
									:name="`assessment-question-${currentQuestionIndex}`"
									:checked="isSelected(currentQuestionIndex, answerIndex)"
									@change="toggleAnswer(currentQuestionIndex, answerIndex)"
								/>
								<div class="w-full flex items-start justify-between gap-3">
									<span class="text-sm text-gray-800">
										<strong class="mr-1">{{ answer.answerLabel || optionLabel(answerIndex) }}</strong>
										{{ answer.answer }}
									</span>
									<span
										v-if="shouldShowAnswerCorrectness(currentQuestionIndex, answerIndex)"
										class="shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
										:class="getAnswerCorrectnessBadgeClass(answer)"
									>
										{{ getAnswerCorrectnessLabel(answer) }}
									</span>
								</div>
							</label>
							<div
								v-if="shouldShowAnswerExplanation(currentQuestionIndex, answerIndex)"
								class="rounded-md border px-3 py-2 text-sm leading-relaxed"
								:class="getAnswerExplanationClass(answer)"
							>
								{{ getAnswerReviewFeedback(answer) }}
							</div>
						</template>
					</div>

					<div v-if="validationMessage" class="mt-3 text-sm text-danger">
						{{ validationMessage }}
					</div>
				</section>

				<footer class="p-4 border-t border-gray-200 flex items-center gap-2">
					<div class="ml-auto flex items-center gap-2">
						<EwButton
							label="Back"
							type="secondary"
							icon="tabler:arrow-narrow-left"
							:disabled="currentQuestionIndex === 0"
							@click="goToPreviousQuestion"
						/>
						<EwButton
							v-if="!isLastQuestion"
							label="Next"
							type="success"
							iconAppend="tabler:arrow-narrow-right"
							@click="goToNextQuestion"
						/>
						<EwButton
							v-else
							label="Submit Assessment"
							type="danger"
							iconAppend="tabler:send"
							@click="submitAssessment"
						/>
					</div>
				</footer>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { messages } from '@/assets/language';
import type { LanguageCodes, QuizQuestionsItem } from '@/types';
import { useAppStore } from '@/stores/app-store';
import InplaceEdit from './InplaceEdit/InplaceEdit.vue';
import EditButton from './EditButton.vue';
import AssessmentResult from './AssessmentResult.vue';
import AssessmentRulesPanel from './AssessmentRulesPanel.vue';

type QuizAnswer = {
	id?: string;
	answer?: string;
	answerLabel?: string;
	explanation?: string;
	isCorrect?: boolean | string;
};

type IllustrationItem = {
	id?: string | number;
	identifier?: string | number;
	imageSrc?: string;
	imageSrcEdit?: string;
	imageTitle?: string;
	imageDescription?: string;
	imageCopyright?: string;
};

type AssessmentRules = {
	scoring?: Record<string, number | string | null | undefined> & {
		totalScore?: number | string;
		totalQuestions?: number | string;
		pointsPerQuestion?: number | string;
		passingScorePercent?: number | string;
	};
	thresholds?: Record<string, number | string | null>;
	Thresholds?: Record<string, number | string | null>;
	criticalRule?: number | string;
	distribution?: Record<string, number | string | null>;
	Distribution?: Record<string, number | string | null>;
	questionDistribution?: Record<string, number | string | null>;
	question_distribution?: Record<string, number | string | null>;
	'Question Distribution'?: Record<string, number | string | null>;
};

type AssessmentNode = {
	questions?: QuizQuestionsItem[];
	title?: string;
	assessmentTitle?: string;
	assessment_title?: string;
	passingPercent?: number;
	leadText?: string;
	leadtext?: string;
	lead_text?: string;
	assessmentRules?: AssessmentRules;
	illustration?: IllustrationItem[];
	metadata?: {
		title?: string;
		assessmentTitle?: string;
		assessment_title?: string;
		leadText?: string;
		leadtext?: string;
		lead_text?: string;
	};
	userConsentText?: string;
	consentText?: string;
	timeLimit?: string | number | null;
	timeLimitUnit?: string;
	timeLimitSeconds?: number | string;
	timeLimitMinutes?: number | string;
	duration?: string | number | null;
	durationUnit?: string;
	durationSeconds?: number | string;
	durationMinutes?: number | string;
	scoringWeights?: {
		competency?: Record<string, number | string>;
		questionType?: Record<string, number | string>;
		typeOfQuestion?: Record<string, number | string>;
		criticalLevel?: Record<string, number | string>;
	};
	criteriaWeights?: {
		competency?: Record<string, number | string>;
		questionType?: Record<string, number | string>;
		typeOfQuestion?: Record<string, number | string>;
		criticalLevel?: Record<string, number | string>;
	};
};

type AssessmentSource = {
	assessment?: AssessmentNode;
	assessmentQuiz?: AssessmentNode;
	mainOutcome?: {
		title?: string;
		leadText?: string;
	};
	metadata?: {
		title?: string;
		leadText?: string;
	};
	leadText?: string;
	title?: string;
	assessmentRules?: AssessmentRules;
	illustration?: IllustrationItem[];
	_links?: {
		assessment?: { href: string };
		assessmentEdit?: { href: string };
		assessmentQuiz?: { href: string };
		assessmentQuizEdit?: { href: string };
	};
};

type EvaluationResult = {
	questionNumber: number;
	questionText: string;
	isCorrect: boolean;
	basePoints: number;
	pointsEarned: number;
	pointsPossible: number;
	competency: string;
	questionType: string;
	criticalLevel: string;
	competencyWeight: number;
	questionTypeWeight: number;
	criticalLevelWeight: number;
	totalWeightMultiplier: number;
	selectedAnswerLabels: string[];
	correctAnswerLabels: string[];
	explanation: string;
	rawCompetency: string;
	rawQuestionType: string;
	rawCriticality: string;
	pointsThreshold: string;
	questionFeedback: string;
	rawIllustrationImageSrc: string;
	rawImageSrc: string;
};

type CriteriaScoreItem = {
	label: string;
	earnedPoints: number;
	totalPoints: number;
	questionCount: number;
	correctAnswers: number;
	scorePercent: number;
};

type AttemptHistoryItem = {
	attempt: number;
	percentage: number;
	earnedPoints: number;
	totalPoints: number;
	correctAnswers: number;
	totalQuestions: number;
	elapsedSeconds: number;
	createdAt: string;
};

const props = defineProps<{
	selectedLanguage?: LanguageCodes;
	overrideURL?: string;
	collectionMode?: string | null;
	jsonData: AssessmentSource;
}>();

const emits = defineEmits<{
	close: [];
	submit: [Record<string, unknown>];
}>();

const { currentLng, currentCollectionMode, currentTestyourself } = useAppStore();
const labels = computed(() => {
	const key = currentLng.value as keyof typeof messages;
	return messages[key] ?? {};
});

const isEnabledFlag = (value: unknown): boolean =>
	['y', 'yes', 'true', '1'].includes(String(value ?? '').trim().toLowerCase());

const normalizeCollectionMode = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_');

const REVIEW_CORRECTNESS_COLLECTION_MODES = new Set([
	'language_review',
	'internal_review',
	'internal_reviewer',
	'authoring'
]);

const activeCollectionMode = computed(() => {
	const candidates = [
		props.collectionMode,
		currentCollectionMode.value,
		(props.jsonData as any)?.config?.id
	];

	for (const candidate of candidates) {
		const normalized = normalizeCollectionMode(candidate);
		if (normalized) {
			return normalized;
		}
	}
	return '';
});

const showAnswerCorrectnessByCollectionMode = computed(() =>
	REVIEW_CORRECTNESS_COLLECTION_MODES.has(activeCollectionMode.value)
);

const showSelectedAnswerExplanationByCollectionMode = computed(() => {
	const mode = activeCollectionMode.value;
	return mode === 'language_review' || mode.startsWith('internal_reviewer') || mode.startsWith('internal_review');
});

const isLoading = ref(false);
const fetchError = ref<string | null>(null);
const fetchedData = ref<AssessmentSource | null>(null);

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const resolveAssessmentNode = (source: unknown): AssessmentNode | null => {
	if (!isObjectRecord(source)) {
		return null;
	}

	const wrappedAssessment = source.assessment;
	if (isObjectRecord(wrappedAssessment)) {
		return wrappedAssessment as AssessmentNode;
	}

	const wrappedQuiz = source.assessmentQuiz;
	if (isObjectRecord(wrappedQuiz)) {
		return wrappedQuiz as AssessmentNode;
	}

	// Some endpoints return assessment payload directly (without assessment/assessmentQuiz wrapper).
	const looksLikeDirectAssessmentNode =
		Array.isArray((source as any).questions) ||
		(source as any).assessmentRules !== undefined ||
		(source as any).passingPercent !== undefined ||
		(source as any).userConsentText !== undefined ||
		(source as any).consentText !== undefined ||
		(source as any).timeLimit !== undefined ||
		(source as any).timeLimitSeconds !== undefined ||
		(source as any).timeLimitMinutes !== undefined ||
		(source as any).duration !== undefined ||
		(source as any).durationSeconds !== undefined ||
		(source as any).durationMinutes !== undefined ||
		(source as any).scoringWeights !== undefined ||
		(source as any).criteriaWeights !== undefined;

	if (looksLikeDirectAssessmentNode) {
		return source as AssessmentNode;
	}

	return null;
};

const resolveAssessmentRulesObject = (value: unknown): AssessmentRules | null => {
	if (!isObjectRecord(value)) {
		return null;
	}

	const hasRuleShape =
		isObjectRecord((value as any).scoring) ||
		isObjectRecord((value as any).thresholds) ||
		isObjectRecord((value as any).Thresholds) ||
		isObjectRecord((value as any).distribution) ||
		isObjectRecord((value as any).Distribution) ||
		isObjectRecord((value as any).questionDistribution) ||
		isObjectRecord((value as any).question_distribution) ||
		isObjectRecord((value as any)['Question Distribution']) ||
		(value as any).criticalRule !== undefined;

	return hasRuleShape ? (value as AssessmentRules) : null;
};

const questions = computed<QuizQuestionsItem[]>(() => {
	const fetchedQuestions = resolveAssessmentNode(fetchedData.value)?.questions;
	if (Array.isArray(fetchedQuestions)) {
		return fetchedQuestions;
	}
	const inlineQuestions = resolveAssessmentNode(props.jsonData)?.questions;
	return Array.isArray(inlineQuestions) ? inlineQuestions : [];
});

const toPlainText = (value: unknown): string => {
	const raw = String(value ?? '').trim();
	if (!raw) {
		return '';
	}

	if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
		const doc = new DOMParser().parseFromString(raw, 'text/html');
		return String(doc.body.textContent ?? '')
			.replace(/\s+/g, ' ')
			.trim();
	}

	return raw
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
};

const formatCriteriaDisplayValue = (value: unknown): string => {
	const text = toPlainText(value);
	if (!text) {
		return '';
	}
	return text
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.split(' ')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join(' ');
};

const readMetadataTitle = (source: unknown): string => {
	if (!source || typeof source !== 'object') {
		return '';
	}
	const metadata = (source as any).metadata;
	if (!metadata || typeof metadata !== 'object') {
		return '';
	}
	return String(
		metadata.title ??
		metadata.assessmentTitle ??
		metadata.assessment_title ??
		''
	).trim();
};

const readMetadataLeadText = (source: unknown): string => {
	if (!source || typeof source !== 'object') {
		return '';
	}
	const metadata = (source as any).metadata;
	if (!metadata || typeof metadata !== 'object') {
		return '';
	}
	return toPlainText(
		metadata.leadText ??
		metadata.leadtext ??
		metadata.lead_text ??
		''
	);
};

const readAssessmentTitleFromNode = (source: AssessmentNode | null): string => {
	if (!source || typeof source !== 'object') {
		return '';
	}

	const directTitle = String(
		source.title ??
		source.assessmentTitle ??
		source.assessment_title ??
		''
	).trim();
	if (directTitle) {
		return directTitle;
	}

	return readMetadataTitle(source);
};

const readAssessmentLeadTextFromNode = (source: AssessmentNode | null): string => {
	if (!source || typeof source !== 'object') {
		return '';
	}

	const fromMetadata = readMetadataLeadText(source);
	if (fromMetadata) {
		return fromMetadata;
	}

	return toPlainText(
		(source as any).leadText ??
		(source as any).leadtext ??
		(source as any).lead_text ??
		''
	);
};

const preferAssessmentLeadTextOnly = computed(() => isEnabledFlag(currentTestyourself.value));

const assessmentTitle = computed(() => {
	const fetchedAssessment = resolveAssessmentNode(fetchedData.value);
	const propsAssessment = resolveAssessmentNode(props.jsonData);

	const titleFromFetchedAssessment = readAssessmentTitleFromNode(fetchedAssessment);
	if (titleFromFetchedAssessment) return titleFromFetchedAssessment;
	const titleFromFetchedMetadata = readMetadataTitle(fetchedData.value);
	if (titleFromFetchedMetadata) return titleFromFetchedMetadata;
	const titleFromPropsAssessment = readAssessmentTitleFromNode(propsAssessment);
	if (titleFromPropsAssessment) return titleFromPropsAssessment;
	const titleFromPropsMetadata = readMetadataTitle(props.jsonData);
	if (titleFromPropsMetadata) return titleFromPropsMetadata;

	const titleFromFetched = String(fetchedData.value?.mainOutcome?.title ?? '').trim();
	if (titleFromFetched) return titleFromFetched;
	const titleFromFetchedRoot = String(fetchedData.value?.title ?? '').trim();
	if (titleFromFetchedRoot) return titleFromFetchedRoot;
	const titleFromProps = String(props.jsonData?.mainOutcome?.title ?? '').trim();
	if (titleFromProps) return titleFromProps;
	const titleFromPropsRoot = String(props.jsonData?.title ?? '').trim();
	if (titleFromPropsRoot) return titleFromPropsRoot;
	return 'Assessment';
});

const assessmentLeadText = computed(() => {
	const fetchedAssessment = resolveAssessmentNode(fetchedData.value);
	const propsAssessment = resolveAssessmentNode(props.jsonData);

	const leadFromFetchedAssessment = readAssessmentLeadTextFromNode(fetchedAssessment);
	if (leadFromFetchedAssessment) return leadFromFetchedAssessment;
	const leadFromPropsAssessment = readAssessmentLeadTextFromNode(propsAssessment);
	if (leadFromPropsAssessment) return leadFromPropsAssessment;

	if (preferAssessmentLeadTextOnly.value) {
		const fallbackMainOutcomeFromFetched = toPlainText(fetchedData.value?.mainOutcome?.leadText);
		if (fallbackMainOutcomeFromFetched) return fallbackMainOutcomeFromFetched;
		return toPlainText(props.jsonData?.mainOutcome?.leadText);
	}

	const leadFromFetchedMetadata = readMetadataLeadText(fetchedData.value);
	if (leadFromFetchedMetadata) return leadFromFetchedMetadata;
	const leadFromPropsMetadata = readMetadataLeadText(props.jsonData);
	if (leadFromPropsMetadata) return leadFromPropsMetadata;

	const leadFromFetched = toPlainText(fetchedData.value?.mainOutcome?.leadText);
	if (leadFromFetched) return leadFromFetched;
	const leadFromFetchedRoot = toPlainText(fetchedData.value?.leadText);
	if (leadFromFetchedRoot) return leadFromFetchedRoot;
	const leadFromProps = toPlainText(props.jsonData?.mainOutcome?.leadText);
	if (leadFromProps) return leadFromProps;
	return toPlainText(props.jsonData?.leadText);
});

const illustrationList = computed<IllustrationItem[]>(() => {
	const fromFetchedAssessment = resolveAssessmentNode(fetchedData.value)?.illustration;
	if (Array.isArray(fromFetchedAssessment)) {
		return fromFetchedAssessment;
	}
	const fromFetched = fetchedData.value?.illustration;
	if (Array.isArray(fromFetched)) {
		return fromFetched;
	}
	const fromPropsAssessment = resolveAssessmentNode(props.jsonData)?.illustration;
	if (Array.isArray(fromPropsAssessment)) {
		return fromPropsAssessment;
	}
	const fromProps = props.jsonData?.illustration;
	return Array.isArray(fromProps) ? fromProps : [];
});

const normalizeAssessmentRuleKey = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');

const getScoringRuleValue = (keys: string[]): string => {
	const scoring = assessmentRules.value?.scoring;
	if (!isObjectRecord(scoring)) {
		return '';
	}

	const normalizedEntries = new Map<string, unknown>();
	Object.entries(scoring).forEach(([key, value]) => {
		normalizedEntries.set(normalizeAssessmentRuleKey(key), value);
	});

	for (const key of keys) {
		const rawValue = normalizedEntries.get(normalizeAssessmentRuleKey(key));
		const value = String(rawValue ?? '').trim();
		if (value) {
			return value;
		}
	}

	return '';
};

const passingPercent = computed(() => {
	const assessmentFromFetched = resolveAssessmentNode(fetchedData.value);
	const assessmentFromProps = resolveAssessmentNode(props.jsonData);
	const scoringPassingScore = getScoringRuleValue(['passingScorePercent', 'Overall Passing Score', 'overall_passing_score']);
	const candidateSource =
		scoringPassingScore !== ''
			? scoringPassingScore
			: assessmentFromFetched?.passingPercent ?? assessmentFromProps?.passingPercent ?? 70;
	const candidate = Number(candidateSource);
	if (!Number.isFinite(candidate)) return 70;
	return Math.max(0, Math.min(100, candidate));
});

const assessmentRules = computed<AssessmentRules | null>(() => {
	const fetchedAssessment = resolveAssessmentNode(fetchedData.value);
	const propsAssessment = resolveAssessmentNode(props.jsonData);

	const candidates: unknown[] = [
		(fetchedData.value as any)?.assessmentRules,
		(fetchedAssessment as any)?.assessmentRules,
		(props.jsonData as any)?.assessmentRules,
		(propsAssessment as any)?.assessmentRules
	];

	for (const candidate of candidates) {
		const resolved = resolveAssessmentRulesObject(candidate);
		if (resolved) {
			return resolved;
		}
	}

	return null;
});

const scoringTotalScore = computed(() => getScoringRuleValue(['totalScore', 'Total score', 'Total Score']));
const scoringPointsPerQuestion = computed(() => getScoringRuleValue(['pointsPerQuestion', 'Points per Question']));

const formatDuration = (totalSeconds: number): string => {
	const safeSeconds = Math.max(0, Math.floor(totalSeconds));
	const minutes = Math.floor(safeSeconds / 60)
		.toString()
		.padStart(2, '0');
	const seconds = Math.floor(safeSeconds % 60)
		.toString()
		.padStart(2, '0');
	return `${minutes}:${seconds}`;
};

const assessmentConfig = computed<Partial<AssessmentNode>>(() => {
	const fromFetched = resolveAssessmentNode(fetchedData.value);
	if (fromFetched) {
		return fromFetched;
	}
	const fromProps = resolveAssessmentNode(props.jsonData);
	return fromProps ?? {};
});

const isNoneValue = (value: unknown): boolean => {
	const normalized = String(value ?? '').trim().toLowerCase();
	return normalized === '' || normalized === 'none' || normalized === 'null' || normalized === 'no-limit' || normalized === 'nolimit';
};

const parsePositiveNumber = (value: unknown): number | null => {
	const candidate = Number(value);
	if (!Number.isFinite(candidate) || candidate <= 0) {
		return null;
	}
	return candidate;
};

const parseDurationFromText = (value: unknown): number | null => {
	const raw = String(value ?? '').trim().toLowerCase();
	if (!raw || isNoneValue(raw)) {
		return null;
	}

	if (/^\d+:\d{1,2}(:\d{1,2})?$/.test(raw)) {
		const chunks = raw.split(':').map((item) => Number(item));
		if (chunks.some((item) => !Number.isFinite(item) || item < 0)) {
			return null;
		}
		if (chunks.length === 2) {
			return chunks[0] * 60 + chunks[1];
		}
		return chunks[0] * 3600 + chunks[1] * 60 + chunks[2];
	}

	const withUnit = raw.match(/^(\d+(?:\.\d+)?)\s*(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours)$/);
	if (withUnit) {
		const amount = Number(withUnit[1]);
		if (!Number.isFinite(amount) || amount <= 0) {
			return null;
		}
		const unit = withUnit[2];
		if (unit.startsWith('h')) return Math.round(amount * 3600);
		if (unit.startsWith('m')) return Math.round(amount * 60);
		return Math.round(amount);
	}

	return null;
};

const parseDurationSeconds = (config: Partial<AssessmentNode> | null | undefined): number | null => {
	if (!config) {
		return null;
	}

	const explicitSeconds = [
		config.timeLimitSeconds,
		config.durationSeconds
	];
	for (const item of explicitSeconds) {
		const parsed = parsePositiveNumber(item);
		if (parsed !== null) {
			return Math.round(parsed);
		}
	}

	const explicitMinutes = [
		config.timeLimitMinutes,
		config.durationMinutes
	];
	for (const item of explicitMinutes) {
		const parsed = parsePositiveNumber(item);
		if (parsed !== null) {
			return Math.round(parsed * 60);
		}
	}

	const genericValue = config.timeLimit ?? config.duration;
	if (isNoneValue(genericValue)) {
		return null;
	}

	const parsedText = parseDurationFromText(genericValue);
	if (parsedText !== null) {
		return parsedText;
	}

	const genericNumber = parsePositiveNumber(genericValue);
	if (genericNumber === null) {
		return null;
	}

	const unit = String(config.timeLimitUnit ?? config.durationUnit ?? '').trim().toLowerCase();
	if (unit.startsWith('sec')) {
		return Math.round(genericNumber);
	}
	return Math.round(genericNumber * 60);
};

const assessmentTimeLimitSeconds = computed(() => {
	const fromAssessmentConfig = parseDurationSeconds(assessmentConfig.value);
	if (fromAssessmentConfig !== null) {
		return fromAssessmentConfig;
	}

	const fallbackSources = [fetchedData.value as any, props.jsonData as any];
	for (const source of fallbackSources) {
		if (!source || typeof source !== 'object') {
			continue;
		}
		const fallbackConfig: AssessmentNode = {
			timeLimit: source.assessmentTimeLimit ?? source.timeLimit ?? source.duration,
			timeLimitUnit: source.assessmentTimeLimitUnit ?? source.timeLimitUnit ?? source.durationUnit,
			timeLimitSeconds: source.assessmentTimeLimitSeconds ?? source.timeLimitSeconds,
			timeLimitMinutes: source.assessmentTimeLimitMinutes ?? source.timeLimitMinutes,
			durationSeconds: source.durationSeconds,
			durationMinutes: source.durationMinutes
		};
		const parsed = parseDurationSeconds(fallbackConfig);
		if (parsed !== null) {
			return parsed;
		}
	}

	return null;
});
const hasTimeLimit = computed(() => assessmentTimeLimitSeconds.value !== null);
const formattedTimeLimit = computed(() => {
	if (!hasTimeLimit.value) {
		return 'No time limit';
	}
	return formatDuration(assessmentTimeLimitSeconds.value ?? 0);
});
const userConsentText = computed(() => {
	const fromConfig = [
		assessmentConfig.value?.userConsentText,
		assessmentConfig.value?.consentText,
		(fetchedData.value as any)?.userConsentText,
		(props.jsonData as any)?.userConsentText
	]
		.map((value) => String(value ?? '').trim())
		.find((value) => value.length > 0);
	if (fromConfig) {
		return fromConfig;
	}
	return 'Before starting, please confirm that you consent to assessment session monitoring for quality assurance, compliance, and academic integrity.';
});

const DEFAULT_COMPETENCY_WEIGHTS: Record<string, number> = {
	knowledge: 1,
	application: 1,
	analysis: 1,
	synthesis: 1,
	evaluation: 1,
	default: 1
};

const DEFAULT_QUESTION_TYPE_WEIGHTS: Record<string, number> = {
	mcq: 1,
	multiplechoice: 1,
	singlechoice: 1,
	multiselect: 1,
	casebased: 1,
	scenario: 1,
	shortanswer: 1,
	truefalse: 1,
	default: 1
};

const DEFAULT_CRITICAL_LEVEL_WEIGHTS: Record<string, number> = {
	low: 1,
	medium: 1,
	high: 1,
	critical: 1,
	default: 1
};

const normalizeWeightKey = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');

const roundPoints = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const toPositiveWeight = (value: unknown): number | null => {
	const candidate = Number(value);
	if (!Number.isFinite(candidate) || candidate <= 0) {
		return null;
	}
	return candidate;
};

const readWeightMap = (source: unknown): Record<string, number> => {
	if (!source || typeof source !== 'object') {
		return {};
	}

	const output: Record<string, number> = {};
	Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
		const normalizedKey = normalizeWeightKey(key);
		const parsed = toPositiveWeight(value);
		if (normalizedKey && parsed !== null) {
			output[normalizedKey] = parsed;
		}
	});
	return output;
};

const scoringWeightSource = computed(() => {
	return (
		assessmentConfig.value?.scoringWeights ??
		assessmentConfig.value?.criteriaWeights ??
		({})
	) as Record<string, unknown>;
});

const competencyWeightMap = computed(() =>
	readWeightMap((scoringWeightSource.value as any)?.competency ?? {})
);
const questionTypeWeightMap = computed(() =>
	readWeightMap(
		(scoringWeightSource.value as any)?.typeOfQuestion ??
		(scoringWeightSource.value as any)?.questionType ??
		{}
	)
);
const criticalLevelWeightMap = computed(() =>
	readWeightMap((scoringWeightSource.value as any)?.criticalLevel ?? {})
);

const resolveWeight = (label: string, configured: Record<string, number>, defaults: Record<string, number>): number => {
	const normalized = normalizeWeightKey(label);
	const fallbackDefault = defaults.default ?? 1;
	if (normalized && configured[normalized] !== undefined) {
		return configured[normalized];
	}
	if (normalized && defaults[normalized] !== undefined) {
		return defaults[normalized];
	}
	if (configured.default !== undefined) {
		return configured.default;
	}
	return fallbackDefault;
};

const getFirstTextValue = (source: unknown, keys: string[]): string => {
	if (!source || typeof source !== 'object') {
		return '';
	}
	const asRecord = source as Record<string, unknown>;
	for (const key of keys) {
		const raw = asRecord[key];
		const value = String(raw ?? '').trim();
		if (value) {
			return value;
		}
	}
	return '';
};

const COMPETENCY_RULE_LABEL_ALIASES: Record<string, string[]> = {
	communication: ['crossaudiencecommunication'],
	understanding: ['caseunderstanding'],
	limitsescalation: ['rolelimitsescalation'],
	rolebasedaction: ['rolebasedcompetency']
};

const getAssessmentRuleSection = (aliases: string[]): Record<string, unknown> => {
	const rules = assessmentRules.value;
	if (!isObjectRecord(rules)) {
		return {};
	}

	for (const alias of aliases) {
		const section = (rules as Record<string, unknown>)[alias];
		if (isObjectRecord(section)) {
			return section;
		}
	}

	const normalizedAliases = new Set(aliases.map((alias) => normalizeAssessmentRuleKey(alias)));
	for (const [key, value] of Object.entries(rules)) {
		if (normalizedAliases.has(normalizeAssessmentRuleKey(key)) && isObjectRecord(value)) {
			return value;
		}
	}

	return {};
};

const getCompetencyRuleLabel = (value: unknown): string => {
	const normalizedValue = normalizeAssessmentRuleKey(value);
	if (!normalizedValue) {
		return '';
	}

	const matchKeys = new Set([
		normalizedValue,
		...(COMPETENCY_RULE_LABEL_ALIASES[normalizedValue] ?? [])
	]);
	const ruleSections = [
		getAssessmentRuleSection(['Thresholds', 'thresholds']),
		getAssessmentRuleSection(['Question Distribution', 'questionDistribution', 'question_distribution', 'distribution', 'Distribution'])
	];

	for (const section of ruleSections) {
		for (const key of Object.keys(section)) {
			const normalizedKey = normalizeAssessmentRuleKey(key);
			if (!normalizedKey.includes('critical') && matchKeys.has(normalizedKey)) {
				return key;
			}
		}
	}

	return '';
};

const resolveCompetencyDisplayValue = (question: QuizQuestionsItem | undefined): string => {
	const q = (question ?? {}) as Record<string, unknown>;
	const rawValue = getFirstTextValue(q, [
		'targetOfCompetencyIdentifier',
		'competency',
		'compentency',
		'competence',
		'competencyName',
		'domain',
		'skill'
	]);
	const ruleLabel = getCompetencyRuleLabel(rawValue);
	return ruleLabel || formatCriteriaDisplayValue(rawValue);
};

const getQuestionCriteria = (question: QuizQuestionsItem | undefined) => {
	const q = (question ?? {}) as Record<string, unknown>;
	const competency =
		getFirstTextValue(q, ['targetOfCompetencyIdentifier', 'competency', 'compentency', 'competence', 'competencyName', 'domain', 'skill']) || 'General';
	const questionType =
		getFirstTextValue(q, ['questionTypeIdentifier', 'typeOfQuestionIdentifier', 'typeOfQuestion', 'questionType', 'question_type', 'type', 'typeQuestion']) || 'Standard';
	const criticalLevel =
		getFirstTextValue(q, ['criticalLevelIdentifier', 'criticalLevel', 'criticality', 'critical_level', 'riskLevel', 'priority']) || 'Normal';

	return {
		competency,
		questionType,
		criticalLevel
	};
};

type QuestionDisplayMeta = {
	competency: string;
	questionType: string;
	criticality: string;
	pointsThreshold: string;
	questionFeedback: string;
	illustrationImageSrc: string;
	imageSrc: string;
};

const getQuestionDisplayMeta = (question: QuizQuestionsItem | undefined): QuestionDisplayMeta => {
	const q = (question ?? {}) as Record<string, unknown>;
	const resolvedCriticality =
		toPlainText(getFirstTextValue(q, ['criticalLevelIdentifier', 'criticality', 'criticalLevel', 'critical_level', 'riskLevel', 'priority'])) ||
		toPlainText(getQuestionCriteria(question).criticalLevel);
	return {
		questionFeedback: toPlainText(q.questionFeedback),
		competency: resolveCompetencyDisplayValue(question),
		questionType: formatCriteriaDisplayValue(getFirstTextValue(q, ['questionTypeIdentifier', 'questionType', 'typeOfQuestion', 'question_type', 'typeQuestion'])),
		criticality: formatCriteriaDisplayValue(resolvedCriticality),
		pointsThreshold: toPlainText(getFirstTextValue(q, ['pointsThreshold', 'points', 'questionPoints'])),
		illustrationImageSrc: toPlainText(getFirstTextValue(q, ['illustrationImageSrc', 'imageSrc'])),
		imageSrc: toPlainText(getFirstTextValue(q, ['imageSrc']))
	};
};

const selectedAnswers = ref<number[][]>([]);
const currentQuestionIndex = ref(0);
const validationMessage = ref('');
const isCompleted = ref(false);
const hasStarted = ref(false);
const hasMonitoringConsent = ref(false);
const startValidationMessage = ref('');
const elapsedSeconds = ref(0);
const remainingSeconds = ref<number | null>(null);
const attemptHistory = ref<AttemptHistoryItem[]>([]);
let timerHandle: ReturnType<typeof setInterval> | null = null;

const totalQuestions = computed(() => questions.value.length);
const currentQuestionNumber = computed(() => Math.min(currentQuestionIndex.value + 1, Math.max(totalQuestions.value, 1)));
const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);
const isLanguageReviewAssessmentMode = computed(() => activeCollectionMode.value === 'language_review');
const currentQuestionEditUrl = computed(() =>
	String((currentQuestion.value as any)?._links?.self?.href ?? '').trim()
);
const currentQuestionEditId = computed(() =>
	String((currentQuestion.value as any)?.id ?? (currentQuestion.value as any)?.identifier ?? currentQuestionNumber.value)
);
const showQuestionFullSliderEdit = computed(() =>
	isLanguageReviewAssessmentMode.value && Boolean(currentQuestionEditUrl.value)
);

const normalizeId = (value: unknown): string => String(value ?? '').trim().toLowerCase();
const parseBooleanFlag = (value: unknown): boolean | null => {
	const normalized = String(value ?? '').trim().toLowerCase();
	if (!normalized) {
		return null;
	}
	if (['y', 'yes', 'true', '1'].includes(normalized)) {
		return true;
	}
	if (['n', 'no', 'false', '0'].includes(normalized)) {
		return false;
	}
	return null;
};

const getQuestionSelectedImageId = (question: QuizQuestionsItem | undefined): string => {
	const q = (question ?? {}) as Record<string, unknown>;
	const candidates = [
		q.selectedIllustrationId,
		q.selectedImageId,
		q.illustrationIdentifier,
		q.illustrationId,
		q.imageId,
		q.selectedIllustrationIdentifier
	];

	for (const candidate of candidates) {
		const normalized = normalizeId(candidate);
		if (normalized) {
			return normalized;
		}
	}
	return '';
};

const getQuestionDirectImageSrc = (question: QuizQuestionsItem | undefined): string => {
	const q = (question ?? {}) as Record<string, unknown>;
	const directCandidates = [
		q.illustrationImageSrc,
		q.imageSrc,
		q.selectedImageSrc,
		q.questionImage,
		q.questionImageSrc,
		(q.selectedIllustration as Record<string, unknown> | undefined)?.imageSrc,
		(q.selectedIllustration as Record<string, unknown> | undefined)?.imageSrcEdit,
		(q.illustration as Record<string, unknown> | undefined)?.imageSrc,
		(q.illustration as Record<string, unknown> | undefined)?.imageSrcEdit
	];

	for (const candidate of directCandidates) {
		const value = String(candidate ?? '').trim();
		if (value) {
			return value;
		}
	}
	return '';
};

const getQuestionDirectImageTitle = (question: QuizQuestionsItem | undefined): string => {
	const q = (question ?? {}) as Record<string, unknown>;
	const directCandidates = [
		q.imageTitle,
		q.selectedImageTitle,
		q.questionImageTitle,
		q.illustrationImageTitle,
		q.illustrationDescription,
		(q.selectedIllustration as Record<string, unknown> | undefined)?.imageTitle,
		(q.selectedIllustration as Record<string, unknown> | undefined)?.imageDescription,
		(q.illustration as Record<string, unknown> | undefined)?.imageTitle,
		(q.illustration as Record<string, unknown> | undefined)?.imageDescription
	];

	for (const candidate of directCandidates) {
		const value = String(candidate ?? '').trim();
		if (value) {
			return value;
		}
	}
	return '';
};

const resolveQuestionImageFromIllustrations = (question: QuizQuestionsItem | undefined): string => {
	const selectedId = getQuestionSelectedImageId(question);
	if (!selectedId) {
		return '';
	}

	const match = illustrationList.value.find((item) => {
		const itemId = normalizeId(item.id);
		const itemIdentifier = normalizeId(item.identifier);
		return selectedId === itemId || selectedId === itemIdentifier;
	});

	if (!match) {
		return '';
	}

	return String(match.imageSrc ?? match.imageSrcEdit ?? '').trim();
};

const resolveQuestionImageTitleFromIllustrations = (question: QuizQuestionsItem | undefined): string => {
	const selectedId = getQuestionSelectedImageId(question);
	if (!selectedId) {
		return '';
	}

	const match = illustrationList.value.find((item) => {
		const itemId = normalizeId(item.id);
		const itemIdentifier = normalizeId(item.identifier);
		return selectedId === itemId || selectedId === itemIdentifier;
	});

	if (!match) {
		return '';
	}

	return String(match.imageTitle ?? match.imageDescription ?? '').trim();
};

const currentQuestionImageSrc = computed(() => {
	const hasIllustrationFlag = parseBooleanFlag((currentQuestion.value as any)?.hasIllustration);
	const direct = getQuestionDirectImageSrc(currentQuestion.value);
	if (direct) {
		return direct;
	}
	if (hasIllustrationFlag === false) {
		return '';
	}

	const fromIllustrationList = resolveQuestionImageFromIllustrations(currentQuestion.value);
	if (fromIllustrationList) {
		return fromIllustrationList;
	}

	return '';
});

const currentQuestionImageTitle = computed(() => {
	const hasIllustrationFlag = parseBooleanFlag((currentQuestion.value as any)?.hasIllustration);
	const direct = getQuestionDirectImageTitle(currentQuestion.value);
	if (direct) {
		return direct;
	}
	if (hasIllustrationFlag === false) {
		return '';
	}

	const fromIllustrationList = resolveQuestionImageTitleFromIllustrations(currentQuestion.value);
	if (fromIllustrationList) {
		return fromIllustrationList;
	}

	return '';
});

const questionImageError = ref(false);
const hasDisplayableQuestionImage = computed(() =>
	Boolean(currentQuestionImageSrc.value) && !questionImageError.value
);
const hasQuestionImageTitle = computed(() => Boolean(currentQuestionImageTitle.value));
const displayedQuestionImageAlt = computed(() =>
	currentQuestionImageTitle.value || 'Question illustration'
);

const handleQuestionImageError = () => {
	questionImageError.value = true;
};

watch(currentQuestionIndex, () => {
	questionImageError.value = false;
});

const currentAnswers = computed<QuizAnswer[]>(() => {
	const answers = currentQuestion.value?.answers;
	return Array.isArray(answers) ? (answers as QuizAnswer[]) : [];
});

const currentQuestionMeta = computed(() => {
	return getQuestionDisplayMeta(currentQuestion.value);
});

const hasCurrentQuestionMeta = computed(() =>
	Boolean(
		currentQuestionMeta.value.competency ||
		currentQuestionMeta.value.questionType ||
		currentQuestionMeta.value.criticality ||
		currentQuestionMeta.value.pointsThreshold
	)
);

const isCurrentQuestionCritical = computed(() => {
	const normalized = String(currentQuestionMeta.value.criticality ?? '')
		.trim()
		.toLowerCase();
	if (!normalized) {
		return false;
	}
	return normalized === 'critical' || normalized.includes('critical');
});

const currentCriticalityBadgeClass = computed(() =>
	isCurrentQuestionCritical.value
		? 'bg-red-100 text-red-700 border-red-200'
		: 'bg-green-100 text-green-700 border-green-200'
);

const getQuestionPoints = (question: QuizQuestionsItem | undefined): number => {
	const candidates = [
		(question as any)?.points,
		(question as any)?.pointsThreshold,
		(question as any)?.questionPoints
	];
	for (const candidate of candidates) {
		const value = Number(candidate);
		if (Number.isFinite(value) && value > 0) {
			return value;
		}
	}
	const pointsPerQuestion = Number(scoringPointsPerQuestion.value);
	if (Number.isFinite(pointsPerQuestion) && pointsPerQuestion > 0) {
		return pointsPerQuestion;
	}
	return 1;
};

type QuestionScoreDetails = {
	basePoints: number;
	weightedPoints: number;
	competency: string;
	questionType: string;
	criticalLevel: string;
	competencyWeight: number;
	questionTypeWeight: number;
	criticalLevelWeight: number;
	totalWeightMultiplier: number;
};

const getQuestionScoreDetails = (question: QuizQuestionsItem | undefined): QuestionScoreDetails => {
	const basePoints = getQuestionPoints(question);
	const { competency, questionType, criticalLevel } = getQuestionCriteria(question);
	const q = (question ?? {}) as Record<string, unknown>;
	const explicitCompetencyWeight = toPositiveWeight(
		getFirstTextValue(q, ['competencyWeight', 'compentencyWeight', 'weightCompetency'])
	);
	const explicitQuestionTypeWeight = toPositiveWeight(
		getFirstTextValue(q, ['questionTypeWeight', 'typeOfQuestionWeight', 'weightQuestionType'])
	);
	const explicitCriticalLevelWeight = toPositiveWeight(
		getFirstTextValue(q, ['criticalLevelWeight', 'weightCriticalLevel', 'criticalWeight'])
	);

	const competencyWeight =
		explicitCompetencyWeight ??
		resolveWeight(competency, competencyWeightMap.value, DEFAULT_COMPETENCY_WEIGHTS);
	const questionTypeWeight =
		explicitQuestionTypeWeight ??
		resolveWeight(questionType, questionTypeWeightMap.value, DEFAULT_QUESTION_TYPE_WEIGHTS);
	const criticalLevelWeight =
		explicitCriticalLevelWeight ??
		resolveWeight(criticalLevel, criticalLevelWeightMap.value, DEFAULT_CRITICAL_LEVEL_WEIGHTS);
	const totalWeightMultiplier = competencyWeight * questionTypeWeight * criticalLevelWeight;
	const weightedPoints = roundPoints(basePoints * totalWeightMultiplier);

	return {
		basePoints,
		weightedPoints,
		competency,
		questionType,
		criticalLevel,
		competencyWeight: roundPoints(competencyWeight),
		questionTypeWeight: roundPoints(questionTypeWeight),
		criticalLevelWeight: roundPoints(criticalLevelWeight),
		totalWeightMultiplier: roundPoints(totalWeightMultiplier)
	};
};

const getCorrectIndexes = (question: QuizQuestionsItem | undefined): number[] => {
	const answers = Array.isArray(question?.answers) ? question.answers : [];
	const indexes: number[] = [];
	answers.forEach((answer, index) => {
		if (parseBooleanFlag(answer?.isCorrect) === true) {
			indexes.push(index);
		}
	});
	return indexes;
};

const currentQuestionIsMulti = computed(() => getCorrectIndexes(currentQuestion.value).length > 1);
const isLastQuestion = computed(() => currentQuestionIndex.value >= totalQuestions.value - 1);

const answeredCount = computed(() =>
	selectedAnswers.value.reduce((acc, entry) => acc + (entry.length > 0 ? 1 : 0), 0)
);
const unansweredCount = computed(() => Math.max(0, totalQuestions.value - answeredCount.value));
const progressWidth = computed(() => {
	if (totalQuestions.value === 0) return '0%';
	return `${Math.round((answeredCount.value / totalQuestions.value) * 100)}%`;
});

const optionLabel = (index: number): string => `${String.fromCharCode(65 + index)}:`;

const normalizeSelection = (value: number[]): number[] => {
	return Array.from(new Set(value)).sort((a, b) => a - b);
};

const isExactSelection = (selected: number[], correct: number[]): boolean => {
	const normalizedSelected = normalizeSelection(selected);
	const normalizedCorrect = normalizeSelection(correct);
	if (normalizedSelected.length !== normalizedCorrect.length) {
		return false;
	}
	return normalizedSelected.every((value, index) => value === normalizedCorrect[index]);
};

const isSelected = (questionIndex: number, answerIndex: number): boolean => {
	return selectedAnswers.value[questionIndex]?.includes(answerIndex) ?? false;
};

const isAnswerCorrect = (answer: QuizAnswer | undefined): boolean =>
	parseBooleanFlag(answer?.isCorrect) === true;

const getAnswerCorrectnessLabel = (answer: QuizAnswer | undefined): string =>
	isAnswerCorrect(answer) ? 'Correct' : 'Incorrect';

const getAnswerCorrectnessBadgeClass = (answer: QuizAnswer | undefined): string =>
	isAnswerCorrect(answer) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

const getAnswerExplanationClass = (answer: QuizAnswer | undefined): string =>
	isAnswerCorrect(answer)
		? 'border-green-200 bg-green-50 text-green-800'
		: 'border-red-200 bg-red-50 text-red-800';

const getAnswerExplanation = (answer: QuizAnswer | undefined): string =>
	toPlainText((answer as any)?.explanation ?? (answer as any)?.answerFeedback ?? '');

const stripCorrectnessPrefix = (value: string): string =>
	value.replace(/^(correct|incorrect)\s*:\s*/i, '').trim();

const getAnswerReviewFeedback = (answer: QuizAnswer | undefined): string => {
	const correctnessLabel = getAnswerCorrectnessLabel(answer);
	const explanation = stripCorrectnessPrefix(getAnswerExplanation(answer));
	return explanation ? `${correctnessLabel}: ${explanation}` : correctnessLabel;
};

const shouldShowAnswerExplanation = (questionIndex: number, answerIndex: number): boolean => {
	if (!showSelectedAnswerExplanationByCollectionMode.value) {
		return false;
	}
	if (!isSelected(questionIndex, answerIndex)) {
		return false;
	}
	return true;
};

const shouldShowAnswerCorrectness = (questionIndex: number, answerIndex: number): boolean => {
	if (!showAnswerCorrectnessByCollectionMode.value) {
		return false;
	}
	if (showSelectedAnswerExplanationByCollectionMode.value) {
		return false;
	}
	return true;
};

const toggleAnswer = (questionIndex: number, answerIndex: number) => {
	validationMessage.value = '';
	const selected = selectedAnswers.value[questionIndex] ?? [];
	if (currentQuestionIsMulti.value) {
		selectedAnswers.value[questionIndex] = selected.includes(answerIndex)
			? selected.filter((item) => item !== answerIndex)
			: [...selected, answerIndex];
		return;
	}
	selectedAnswers.value[questionIndex] = [answerIndex];
};

const canProceedCurrentQuestion = computed(() => {
	return (selectedAnswers.value[currentQuestionIndex.value]?.length ?? 0) > 0;
});

const goToPreviousQuestion = () => {
	validationMessage.value = '';
	if (currentQuestionIndex.value > 0) {
		currentQuestionIndex.value -= 1;
	}
};

const goToNextQuestion = () => {
	if (!canProceedCurrentQuestion.value) {
		validationMessage.value = 'Please select an answer before continuing.';
		return;
	}
	validationMessage.value = '';
	if (!isLastQuestion.value) {
		currentQuestionIndex.value += 1;
	}
};

const formattedElapsed = computed(() => formatDuration(elapsedSeconds.value));

const startAssessment = () => {
	if (!hasMonitoringConsent.value) {
		startValidationMessage.value = 'Please confirm consent before starting the assessment.';
		return;
	}
	startValidationMessage.value = '';
	validationMessage.value = '';
	hasStarted.value = true;
	isCompleted.value = false;
	elapsedSeconds.value = 0;
	remainingSeconds.value = hasTimeLimit.value ? assessmentTimeLimitSeconds.value : null;
	startTimer();
};

const evaluationResults = computed<EvaluationResult[]>(() => {
	return questions.value.map((question, index) => {
		const selected = selectedAnswers.value[index] ?? [];
		const correct = getCorrectIndexes(question);
		const answers = Array.isArray(question.answers) ? (question.answers as QuizAnswer[]) : [];
		const isCorrect = isExactSelection(selected, correct);
		const scoreDetails = getQuestionScoreDetails(question);
		const displayMeta = getQuestionDisplayMeta(question);
		const pointsPossible = scoreDetails.weightedPoints;
		const pointsEarned = isCorrect ? pointsPossible : 0;
		const selectedAnswerLabels = selected.map((answerIndex) => {
			const answer = answers[answerIndex];
			return answer?.answerLabel || optionLabel(answerIndex);
		});
		const correctAnswerLabels = correct.map((answerIndex) => {
			const answer = answers[answerIndex];
			return answer?.answerLabel || optionLabel(answerIndex);
		});
		const explanationFromCorrect = correct
			.map((answerIndex) => String(answers[answerIndex]?.explanation ?? '').trim())
			.find((value) => value.length > 0) ?? '';
		return {
			questionNumber: index + 1,
			questionText: String(question.question ?? ''),
			isCorrect,
			basePoints: scoreDetails.basePoints,
			pointsEarned,
			pointsPossible,
			competency: scoreDetails.competency,
			questionType: scoreDetails.questionType,
			criticalLevel: scoreDetails.criticalLevel,
			competencyWeight: scoreDetails.competencyWeight,
			questionTypeWeight: scoreDetails.questionTypeWeight,
			criticalLevelWeight: scoreDetails.criticalLevelWeight,
			totalWeightMultiplier: scoreDetails.totalWeightMultiplier,
			selectedAnswerLabels,
			correctAnswerLabels,
			explanation: explanationFromCorrect,
			rawCompetency: displayMeta.competency,
			rawQuestionType: displayMeta.questionType,
			rawCriticality: displayMeta.criticality,
			pointsThreshold: displayMeta.pointsThreshold,
			questionFeedback: displayMeta.questionFeedback,
			rawIllustrationImageSrc: displayMeta.illustrationImageSrc,
			rawImageSrc: displayMeta.imageSrc
		};
	});
});

const correctAnswers = computed(() => evaluationResults.value.filter((item) => item.isCorrect).length);
const totalPoints = computed(() => evaluationResults.value.reduce((acc, item) => acc + item.pointsPossible, 0));
const earnedPoints = computed(() => evaluationResults.value.reduce((acc, item) => acc + item.pointsEarned, 0));
const scorePercentage = computed(() => {
	if (totalPoints.value <= 0) return 0;
	return Math.round((earnedPoints.value / totalPoints.value) * 100);
});

const buildCriteriaScores = (
	items: EvaluationResult[],
	key: 'competency' | 'questionType' | 'criticalLevel'
): CriteriaScoreItem[] => {
	const map = new Map<string, CriteriaScoreItem>();
	items.forEach((item) => {
		const label = String(item[key] ?? '').trim() || 'Unspecified';
		const existing = map.get(label) ?? {
			label,
			earnedPoints: 0,
			totalPoints: 0,
			questionCount: 0,
			correctAnswers: 0,
			scorePercent: 0
		};
		existing.earnedPoints = roundPoints(existing.earnedPoints + item.pointsEarned);
		existing.totalPoints = roundPoints(existing.totalPoints + item.pointsPossible);
		existing.questionCount += 1;
		existing.correctAnswers += item.isCorrect ? 1 : 0;
		map.set(label, existing);
	});

	return Array.from(map.values())
		.map((item) => ({
			...item,
			scorePercent:
				item.totalPoints > 0
					? Number(((item.earnedPoints / item.totalPoints) * 100).toFixed(1))
					: 0
		}))
		.sort((a, b) => b.totalPoints - a.totalPoints);
};

const competencyScores = computed(() => buildCriteriaScores(evaluationResults.value, 'competency'));
const questionTypeScores = computed(() => buildCriteriaScores(evaluationResults.value, 'questionType'));
const criticalLevelScores = computed(() => buildCriteriaScores(evaluationResults.value, 'criticalLevel'));
const retakeCount = computed(() => Math.max(0, attemptHistory.value.length - 1));

type SubmitAssessmentOptions = {
	skipCurrentAnswerValidation?: boolean;
	skipUnansweredConfirm?: boolean;
	timedOut?: boolean;
};

const submitAssessment = (options: SubmitAssessmentOptions = {}) => {
	if (!options.skipCurrentAnswerValidation && !canProceedCurrentQuestion.value) {
		validationMessage.value = 'Please select an answer before submitting.';
		return;
	}

	validationMessage.value = '';
	if (!options.skipUnansweredConfirm && unansweredCount.value > 0) {
		const continueSubmit = window.confirm(
			`You have ${unansweredCount.value} unanswered question(s). Submit assessment anyway?`
		);
		if (!continueSubmit) {
			return;
		}
	}

	stopTimer();
	hasStarted.value = false;
	isCompleted.value = true;

	const attemptNumber = attemptHistory.value.length + 1;
	attemptHistory.value = [
		...attemptHistory.value,
		{
			attempt: attemptNumber,
			percentage: scorePercentage.value,
			earnedPoints: earnedPoints.value,
			totalPoints: totalPoints.value,
			correctAnswers: correctAnswers.value,
			totalQuestions: totalQuestions.value,
			elapsedSeconds: elapsedSeconds.value,
			createdAt: new Date().toISOString()
		}
	];

	emits('submit', {
		mode: 'assessment',
		title: assessmentTitle.value,
		totalQuestions: totalQuestions.value,
		answeredQuestions: answeredCount.value,
		correctAnswers: correctAnswers.value,
		totalPoints: totalPoints.value,
		earnedPoints: earnedPoints.value,
		percentage: scorePercentage.value,
		elapsedSeconds: elapsedSeconds.value,
		timeLimitSeconds: assessmentTimeLimitSeconds.value,
		timedOut: options.timedOut === true,
		attempt: attemptNumber,
		retakeCount: Math.max(0, attemptNumber - 1),
		competencyScores: competencyScores.value,
		questionTypeScores: questionTypeScores.value,
		criticalLevelScores: criticalLevelScores.value,
		attemptHistory: attemptHistory.value,
		results: evaluationResults.value
	});
};

const handleRetake = () => {
	stopTimer();
	selectedAnswers.value = questions.value.map(() => []);
	currentQuestionIndex.value = 0;
	validationMessage.value = '';
	startValidationMessage.value = '';
	hasStarted.value = false;
	hasMonitoringConsent.value = false;
	isCompleted.value = false;
	elapsedSeconds.value = 0;
	remainingSeconds.value = hasTimeLimit.value ? assessmentTimeLimitSeconds.value : null;
};

const handleClose = () => {
	emits('close');
};

const startTimer = () => {
	if (timerHandle) {
		clearInterval(timerHandle);
	}
	timerHandle = setInterval(() => {
		if (!hasStarted.value || isCompleted.value) {
			return;
		}

		elapsedSeconds.value += 1;

		if (hasTimeLimit.value && remainingSeconds.value !== null) {
			remainingSeconds.value = Math.max(0, remainingSeconds.value - 1);
			if (remainingSeconds.value === 0) {
				submitAssessment({
					skipCurrentAnswerValidation: true,
					skipUnansweredConfirm: true,
					timedOut: true
				});
			}
		}
	}, 1000);
};

const stopTimer = () => {
	if (timerHandle) {
		clearInterval(timerHandle);
		timerHandle = null;
	}
};

const fetchAssessmentData = async () => {
	if (!props.overrideURL) {
		return;
	}

	isLoading.value = true;
	fetchError.value = null;

	try {
		const response = await fetch(props.overrideURL, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		const contentType = response.headers.get('content-type');
		if (!contentType || !contentType.includes('application/json')) {
			return;
		}

		if (!response.ok) {
			return;
		}

		const data = await response.json();
		if (data && typeof data === 'object') {
			fetchedData.value = data as AssessmentSource;
		}
	} catch {
		fetchError.value = null;
	} finally {
		isLoading.value = false;
	}
};

watch(questions, (newQuestions) => {
	stopTimer();
	selectedAnswers.value = newQuestions.map(() => []);
	currentQuestionIndex.value = 0;
	validationMessage.value = '';
	startValidationMessage.value = '';
	hasStarted.value = false;
	hasMonitoringConsent.value = false;
	isCompleted.value = false;
	attemptHistory.value = [];
	elapsedSeconds.value = 0;
	remainingSeconds.value = hasTimeLimit.value ? assessmentTimeLimitSeconds.value : null;
}, { immediate: true });

watch(assessmentTimeLimitSeconds, (nextLimit) => {
	if (!hasStarted.value) {
		remainingSeconds.value = nextLimit;
	}
});

onMounted(async () => {
	await fetchAssessmentData();
});

onBeforeUnmount(() => {
	stopTimer();
});
</script>
